import api from './api';
import type { CompanyListItem, Pharmacy, CreatePharmacyData, UpdatePharmacyData } from '../types';

export const sysadminService = {
  // Companies
  async getCompanies(): Promise<CompanyListItem[]> {
    const response = await api.get<CompanyListItem[]>('/company/sysadmin/companies');
    return response.data;
  },

  // Users
  async getUsers(params?: { page?: number; pageSize?: number; search?: string }) {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  async createUser(data: Record<string, unknown>) {
    const response = await api.post('/admin/users', data);
    return response.data;
  },

  async updateUser(id: string, data: Record<string, unknown>) {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id: string) {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  async toggleUserStatus(id: string) {
    const response = await api.patch(`/admin/users/${id}/status`);
    return response.data;
  },

  async resetUserPassword(id: string) {
    const response = await api.post(`/admin/users/${id}/password-reset`);
    return response.data;
  },

  async updateUserRoles(id: string, roleIds: string[]) {
    const response = await api.put(`/admin/users/${id}/roles`, { roleIds });
    return response.data;
  },

  // Roles
  async getRoles() {
    const response = await api.get('/admin/roles');
    return response.data;
  },

  async createRole(data: Record<string, unknown>) {
    const response = await api.post('/admin/roles', data);
    return response.data;
  },

  async updateRole(id: string, data: Record<string, unknown>) {
    const response = await api.put(`/admin/roles/${id}`, data);
    return response.data;
  },

  async deleteRole(id: string) {
    const response = await api.delete(`/admin/roles/${id}`);
    return response.data;
  },

  async getRolePermissions(id: string) {
    const response = await api.get(`/admin/roles/${id}/permissions`);
    return response.data;
  },

  async updateRolePermissions(id: string, permissionIds: string[]) {
    const response = await api.put(`/admin/roles/${id}/permissions`, { permissionIds });
    return response.data;
  },

  // Permissions
  async getPermissions() {
    const response = await api.get('/admin/permissions');
    return response.data;
  },

  // Email Templates
  async getEmailTemplates() {
    const response = await api.get('/admin/email-templates');
    return response.data;
  },

  async createEmailTemplate(data: Record<string, unknown>) {
    const response = await api.post('/admin/email-templates', data);
    return response.data;
  },

  async updateEmailTemplate(id: string, data: Record<string, unknown>) {
    const response = await api.put(`/admin/email-templates/${id}`, data);
    return response.data;
  },

  async deleteEmailTemplate(id: string) {
    const response = await api.delete(`/admin/email-templates/${id}`);
    return response.data;
  },

  // Questionnaires
  async getQuestionnaires() {
    const response = await api.get('/admin/questionnaires');
    return response.data;
  },

  async createQuestionnaire(data: Record<string, unknown>) {
    const response = await api.post('/admin/questionnaires', data);
    return response.data;
  },

  async updateQuestionnaire(id: string, data: Record<string, unknown>) {
    const response = await api.put(`/admin/questionnaires/${id}`, data);
    return response.data;
  },

  async deleteQuestionnaire(id: string) {
    const response = await api.delete(`/admin/questionnaires/${id}`);
    return response.data;
  },

  async syncQuestionnaires(companyId: string) {
    const response = await api.post('/admin/questionnaires/sync', { companyId });
    return response.data;
  },

  // Compounds (Medications)
  async getCompounds() {
    const response = await api.get('/admin/compounds');
    return response.data;
  },

  async createCompound(data: Record<string, unknown>) {
    const response = await api.post('/admin/compounds', data);
    return response.data;
  },

  async updateCompound(id: string, data: Record<string, unknown>) {
    const response = await api.put(`/admin/compounds/${id}`, data);
    return response.data;
  },

  // Pharmacies
  async getPharmacies(): Promise<Pharmacy[]> {
    const response = await api.get<Pharmacy[]>('/admin/pharmacies');
    return response.data;
  },

  async createPharmacy(data: CreatePharmacyData): Promise<Pharmacy> {
    const response = await api.post<Pharmacy>('/admin/pharmacies', data);
    return response.data;
  },

  async updatePharmacy(id: string, data: UpdatePharmacyData): Promise<Pharmacy> {
    const response = await api.put<Pharmacy>(`/admin/pharmacies/${id}`, data);
    return response.data;
  },

  async deletePharmacy(id: string) {
    const response = await api.delete(`/admin/pharmacies/${id}`);
    return response.data;
  },
};
